export const VIDRIFT_EMBED_BASE = String(
    process.env.VUE_APP_VIDRIFT_EMBED_BASE || 'https://embed.vidrift.net'
).replace(/\/$/, '')

function positiveInteger(value) {
    const number = Number(value)
    return Number.isSafeInteger(number) && number > 0 ? number : null
}

export function vidriftPlayer(media, { progress = {} } = {}) {
    const tmdbId = positiveInteger(media && media.ids && media.ids.tmdb)
    if (!tmdbId || !['movie', 'series'].includes(media.kind)) return null
    const mediaType = media.kind === 'series' ? 'tv' : 'movie'
    const season = mediaType === 'tv' ? positiveInteger(progress.season) || 1 : null
    const episode = mediaType === 'tv' ? positiveInteger(progress.episode) || 1 : null
    const path = mediaType === 'tv' ? `tv/${tmdbId}/${season}/${episode}` : `movie/${tmdbId}`
    const url = new URL(`${VIDRIFT_EMBED_BASE}/embed/${path}`)
    if (url.protocol !== 'https:') return null
    url.searchParams.set('brand', 'Shinai')
    url.searchParams.set('title', media.title || '')
    url.searchParams.set('exit', '1')
    const currentTime = Number(progress.currentTime)
    return {
        provider: 'vidrift', mode: 'iframe', src: url.href, title: media.title,
        tmdbId: String(tmdbId), mediaType, season, episode,
        episodeOptions: mediaType === 'tv' && Array.isArray(media.episodeOptions) ? media.episodeOptions : [],
        resumeAt: !progress.ended && Number.isFinite(currentTime) && currentTime > 0 ? currentTime : 0,
    }
}

export function selectVidriftEpisode(descriptor, selection) {
    const season = positiveInteger(selection?.season)
    const episode = positiveInteger(selection?.episode)
    if (descriptor?.mediaType !== 'tv' || !positiveInteger(descriptor.tmdbId) || !season || !episode) return null
    const url = new URL(descriptor.src)
    url.pathname = `/embed/tv/${descriptor.tmdbId}/${season}/${episode}`
    return { ...descriptor, src: url.href, season, episode, resumeAt: 0 }
}

// Both the sender window and origin are required: another VidRift iframe on
// the same page must not be able to mark this title watched.
export function readVidriftEvent(event, frameWindow, descriptor, activeEpisode = descriptor) {
    if (!frameWindow || !descriptor || event.source !== frameWindow) return null
    if (event.origin !== new URL(descriptor.src).origin) return null
    const data = event.data
    if (!data || typeof data !== 'object' || String(data.tmdbId) !== descriptor.tmdbId) return null
    const types = ['progress', 'paused', 'ended', 'episode', 'exit']
    const type = String(data.type || '').replace(/^vidrift:/, '')
    if (data.type !== `vidrift:${type}` || !types.includes(type)) return null
    // The live episode picker omits mediaType, although the docs describe it
    // on every event. Its identity is still scoped by window, origin and TMDB.
    if (data.mediaType !== descriptor.mediaType && !(type === 'episode' && data.mediaType == null)) return null
    const episode = descriptor.mediaType === 'tv'
        ? { season: positiveInteger(data.season), episode: positiveInteger(data.episode) }
        : {}
    if (descriptor.mediaType === 'tv') {
        if (!episode.season || !episode.episode) return null
        if (type !== 'episode' && (episode.season !== activeEpisode.season || episode.episode !== activeEpisode.episode)) return null
    }
    if (type === 'exit') return { type }
    if (type === 'episode') {
        if (descriptor.mediaType !== 'tv') return null
        return { type, progress: { ...episode, currentTime: 0, duration: 0, fraction: 0, ended: false } }
    }
    const progress = { ...episode, ended: type === 'ended' }
    if (typeof data.currentTime === 'number' && Number.isFinite(data.currentTime) && data.currentTime >= 0) {
        progress.currentTime = data.currentTime
    }
    if (typeof data.duration === 'number' && Number.isFinite(data.duration) && data.duration > 0) {
        progress.duration = data.duration
        if (progress.currentTime !== undefined) progress.currentTime = Math.min(progress.currentTime, data.duration)
    }
    if (type !== 'ended' && progress.currentTime === undefined) return null
    if (type === 'progress' && !progress.duration) return null
    return { type, progress }
}
